import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { IoClose } from 'react-icons/io5'
import { ALL_APPLICATION_CONFIG_INPUT, CREATE_APPLICATION } from '../../../graphql/mutation'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'react-toastify'
import { GET_ALL_APPLICATIONS } from '../../../graphql/query'

interface CreateBotModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: { name: string; description: string; expiryDate: string }) => void
  fetchBots: () => void
}

const CreateBotModal: React.FC<CreateBotModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  fetchBots
}) => {
  const [configJson, setConfigJson] = useState<any>({})
  const [appId, setAppId] = useState<any>(null)
  const [id, setId] = useState<any>(null)
  const [webhookUrl, setWebhookUrl] = useState<string>('')
  const [shouldSave, setShouldSave] = useState(false)

  const [createApplication] = useMutation(CREATE_APPLICATION, {
    context: {
      headers: {
        'access-control-allow-methods': 'Bot-Builder'
      }
    }
  })

  const [SaveConfigJson] = useMutation(ALL_APPLICATION_CONFIG_INPUT, {
    context: {
      headers: {
        'access-control-allow-methods': 'Bot-Builder'
      }
    }
  })

  const { error, loading, data } = useQuery(GET_ALL_APPLICATIONS, {
    variables: {
      input: {
        appIDString: appId
      }
    },
    context: {
      headers: {
        'access-control-allow-methods': 'Bot-Builder'
      }
    },
    skip: !appId // This will skip the query if appId is null, undefined, or empty string
  })
  const handleSave = async () => {
    console.log('configJson', configJson)
    try {
      const configRes = await SaveConfigJson({
        variables: {
          input: {
            applicationId: id,
            configJson: configJson
          }
        }
      })

      if (
        configRes.data.updateApplicationConfig.status >= 200 &&
        configRes.data.updateApplicationConfig.status < 300
      ) {
        fetchBots()
        onClose()
        toast.success('Bot created successfully.')
      } else {
        toast.error('Failed to save changes.')
      }
    } catch (err) {
      toast.error('Sorry! Unexpected Error.')
      console.error(err)
    }
  }
  console.log(configJson)
  useEffect(() => {
    if (data) {
      console.log(data?.allApplicationId?.[0]?.config)
      setConfigJson(data?.allApplicationId?.[0]?.config)
    }
  }, [data])
  useEffect(() => {
    if (webhookUrl) {
      setConfigJson((prev: any) => ({
        ...prev,
        webhook: {
          ...prev.webhook,
          endpoint: webhookUrl,
          isEnabled: true
        }
      }))
      setShouldSave(true) // trigger save after configJson updates
    }
  }, [webhookUrl])
  useEffect(() => {
    if (shouldSave) {
      handleSave()
      setShouldSave(false) // reset flag
    }
  }, [configJson]) // waits for configJson to update

  if (!isOpen) return null

  if (loading) return <div>Loading...</div>
  if (error) return <div>Error: {error.message}</div>

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const formData = new FormData(e.target as HTMLFormElement)
    const botName = formData.get('botName') as string

    try {
      const res = await createApplication({
        variables: {
          input: {
            botName,
            botDescription: formData.get('botDescription') as string,
            endDate: formData.get('expiryDate') as string,
            templateId: 15
          }
        }
      })

      const result = res?.data?.createApplication
      console.log('GraphQL response:', result?.data?.appIDString)
      setAppId(result?.data?.appIDString)
      setId(result?.data?.id)

      if (result?.status === 200) {
        const resetUrl = `${process.env.REACT_APP_RESUME_PARSER_URL}/ai-chatbots/configure_name`
        console.log('shakya', resetUrl)

        const response = await fetch(resetUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            Authorization: `Bearer ${localStorage.getItem('auth-token')}`,
            accept: 'application/json'
          },
          body: new URLSearchParams({
            bot_name: botName,
            app_id: result?.data?.appIDString,
            description: formData.get('botDescription') as string
          })
        })

        const data = await response.json()
        console.log(data.url)
        setWebhookUrl(data.url)
      } else {
        toast.error(result?.message || 'Application creation failed')
      }
    } catch (error) {
      console.error('Error:', error)
      onClose()
    }
  }

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Create Bot</ModalTitle>
          <CloseButton onClick={onClose}>
            <IoClose size={20} />
          </CloseButton>
        </ModalHeader>
        <ModalForm onSubmit={handleSubmit}>
          <FormGroup>
            <Label>
              Bot Name <Required>*</Required>
            </Label>
            <Input type='text' name='botName' placeholder='Enter bot name' required />
          </FormGroup>

          <FormGroup>
            <Label>
              Bot Description <Required>*</Required>
            </Label>
            <TextArea name='botDescription' placeholder='Enter bot description' required />
          </FormGroup>

          <FormGroup>
            <Label>Expiry Date</Label>
            <Input type='date' name='expiryDate' placeholder='dd/mm/yyyy' />
          </FormGroup>

          <ButtonGroup>
            <CreateButton type='submit'>Create Bot</CreateButton>
          </ButtonGroup>
        </ModalForm>
      </ModalContent>
    </ModalOverlay>
  )
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 0;
`

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #1a1a1a;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  margin: -4px;

  &:hover {
    color: #1f2937;
  }
`

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 24px 24px;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 4px;
`

const Required = styled.span`
  color: #dc2626;
`

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background-color: #f8fafc;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background-color: white;
  }

  &::placeholder {
    color: #94a3b8;
  }
`

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background-color: #f8fafc;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background-color: white;
  }

  &::placeholder {
    color: #94a3b8;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`

const CreateButton = styled.button`
  background-color: #6366f1;
  color: white;
  padding: 8px 24px;
  border-radius: 6px;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #4f46e5;
  }
`

export default CreateBotModal
